import {useState} from 'react';
import styled from 'styled-components';
import {useAcceptSuggestedAction} from '../hooks/useAcceptSuggestedAction';
import {useForecastProjects} from '../hooks/useForecastProjects';

export interface SuggestedActionForPanel {
	id: string;
	title: string;
	rationale: string | null;
	suggestedDueDate: string | null;
	assignee: {id: string; fullName: string | null; initials: string | null} | null;
}

interface Props {
	ticketId: string;
	action: SuggestedActionForPanel;
	onCancel: () => void;
	onCompleted: () => void;
}

const Panel = styled.div`
	margin-top: 8px;
	padding: 12px;
	background: #ffffff;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const Field = styled.label`
	display: block;
	margin-bottom: 10px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const Input = styled.input`
	display: block;
	width: 100%;
	margin-top: 4px;
	padding: 6px 8px;
	font: ${p => p.theme.ticketing.font.body2};
	color: ${p => p.theme.ticketing.color.dark900};
	border: 1px solid ${p => p.theme.ticketing.color.dark100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const Select = styled.select`
	display: block;
	width: 100%;
	margin-top: 4px;
	padding: 6px 8px;
	font: ${p => p.theme.ticketing.font.body2};
	color: ${p => p.theme.ticketing.color.dark900};
	background: #ffffff;
	border: 1px solid ${p => p.theme.ticketing.color.dark100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const TextArea = styled.textarea`
	display: block;
	width: 100%;
	margin-top: 4px;
	padding: 6px 8px;
	min-height: 60px;
	font: ${p => p.theme.ticketing.font.body2};
	color: ${p => p.theme.ticketing.color.dark900};
	border: 1px solid ${p => p.theme.ticketing.color.dark100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const AssigneeRow = styled.label`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 10px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const Actions = styled.div`
	display: flex;
	gap: 8px;
`;
const PrimaryButton = styled.button`
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #ffffff;
	background: ${p => p.theme.ticketing.color.interactivePrimary};
	border: 0;
	border-radius: ${p => p.theme.ticketing.radius.m};
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;
const SecondaryButton = styled.button`
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: transparent;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
	cursor: pointer;
`;

export function AcceptSuggestionPanel({ticketId, action, onCancel, onCompleted}: Props) {
	const [title, setTitle] = useState(action.title);
	const [dueDate, setDueDate] = useState(action.suggestedDueDate ?? '');
	const [description, setDescription] = useState(action.rationale ?? '');
	const [includeAssignee, setIncludeAssignee] = useState(action.assignee != null);
	// Every task must belong to a project, and a ticket carries no project of its
	// own to default to — so the user picks one here. Starts empty; Save stays
	// disabled until a project is chosen.
	const [projectId, setProjectId] = useState('');
	const {projects, loading: projectsLoading} = useForecastProjects();
	const {accept, inFlight} = useAcceptSuggestedAction({onCompleted});

	const onSave = () => {
		if (!projectId) return;
		accept({
			ticketId,
			actionId: action.id,
			title: title.trim(),
			projectId,
			assignedPersonIds: includeAssignee && action.assignee ? [action.assignee.id] : [],
			dueDate: dueDate || null,
			description: description || null,
		});
	};

	return (
		<Panel>
			<Field>
				Title
				<Input aria-label="Title" value={title} onChange={e => setTitle(e.target.value)} />
			</Field>
			<Field>
				Project
				<Select
					aria-label="Project"
					value={projectId}
					onChange={e => setProjectId(e.target.value)}
					disabled={projectsLoading}
				>
					<option value="" disabled>
						Select a project…
					</option>
					{projects.map(p => (
						<option key={p.id} value={p.id}>
							{p.name}
						</option>
					))}
				</Select>
			</Field>
			<Field>
				Due date
				<Input aria-label="Due date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
			</Field>
			<Field>
				Description
				<TextArea aria-label="Description" value={description} onChange={e => setDescription(e.target.value)} />
			</Field>
			{action.assignee && (
				<AssigneeRow>
					<input
						type="checkbox"
						aria-label={`Assign to ${action.assignee.fullName ?? 'suggested assignee'}`}
						checked={includeAssignee}
						onChange={e => setIncludeAssignee(e.target.checked)}
					/>
					<span>{action.assignee.fullName ?? 'Suggested assignee'}</span>
				</AssigneeRow>
			)}
			<Actions>
				<PrimaryButton type="button" disabled={inFlight || title.trim() === '' || !projectId} onClick={onSave}>
					Save
				</PrimaryButton>
				<SecondaryButton type="button" onClick={onCancel}>
					Cancel
				</SecondaryButton>
			</Actions>
		</Panel>
	);
}
